import React, { useState, useContext } from "react";
import {
  Box,
  Typography,
  TextField,
  Button,
  Snackbar,
  IconButton,
  InputAdornment,
  CircularProgress,
  Alert,
  alpha,
  useTheme,
} from "@mui/material";
import { Visibility, VisibilityOff } from "@mui/icons-material";
import { useFormik } from "formik";
import { useNavigate } from "react-router-dom";
import loginValidationSchema from "../../utils/validation-schema/loginValidation";
import { AuthContext } from "../../contexts/AuthContext";
import { getLoginFeedback } from "./loginFeedback";
import AuthFeedback from "./utils/AuthFeedback";
import MicrosoftAuthButton from "./utils/MicrosoftAuthButton";
import MailOutlineIcon from "@mui/icons-material/MailOutline";

const outlinedInputFocusSx = (theme) => ({
  borderRadius: "8px",
  fontSize: "14px",
  transition: "box-shadow 0.2s ease, border-color 0.2s ease",
  backgroundColor: theme.palette.background.paper,
  "&:hover:not(.Mui-error):not(.Mui-disabled) .MuiOutlinedInput-notchedOutline":
    {
      borderColor: theme.palette.primary.main,
    },
  "&.Mui-focused:not(.Mui-error)": {
    boxShadow: `0 0 0 3px ${alpha(theme.palette.primary.main, 0.08)}`,
  },
});

const LoginForm = () => {
  const [snackbarOpen, setSnackbarOpen] = useState(false);
  const [showPassword, setShowPassword] = useState(false);
  const [passwordFocused, setPasswordFocused] = useState(false);
  const [loading, setLoading] = useState(false);
  const [ssoLoading, setSsoLoading] = useState(false);
  const [message, setMessage] = useState(null); // For error messages
  const [isResending, setIsResending] = useState(false);
  const {
    login,
    resendVerificationEmail,
    startMicrosoftSSO,
    isTwoFaSetupPending,
  } = useContext(AuthContext);
  const navigate = useNavigate();
  const theme = useTheme();
  const formik = useFormik({
    initialValues: {
      email: "",
      password: "",
    },
    validationSchema: loginValidationSchema,
    onSubmit: async (values) => {
      if (loading || ssoLoading || isResending || isTwoFaSetupPending) return;
      setLoading(true);
      setMessage(null); // Clear any previous messages
      try {
        const data = await login(values);
        const needs2fa =
          data?.is2faRequired === true || data?.isLoginVerifyRequired === true;
        if (!needs2fa) {
          setSnackbarOpen(true);
        }
      } catch (error) {
        setMessage(getLoginFeedback(error));
      } finally {
        setLoading(false);
      }
    },
  });

  const handleResendEmail = async () => {
    if (loading || ssoLoading || isResending || isTwoFaSetupPending) return;
    const emailToUse = formik.values.email?.trim() || "";
    if (!emailToUse || formik.errors.email) {
      formik.setFieldTouched("email", true, true);
      return;
    }
    setIsResending(true);
    try {
      await resendVerificationEmail(emailToUse);
      setMessage({
        severity: "success",
        title: "Verification email sent",
        message:
          "Check your inbox and spam folder, then follow the link to verify your email before signing in.",
      });
    } catch (error) {
      const cause = error?.error || error;
      const rateLimited = (cause?.response?.status || cause?.status) === 429;
      setMessage({
        severity: "error",
        title: "Verification email could not be sent",
        message: rateLimited
          ? "Too many requests were made. Wait a few minutes before requesting another email."
          : "Please try again. If the email still does not arrive, contact support.",
        canResend: true,
      });
    } finally {
      setIsResending(false);
    }
  };

  const handleSnackbarClose = () => {
    setSnackbarOpen(false);
  };

  const handleClickShowPassword = () => {
    setShowPassword((prev) => !prev);
  };

  const handleEmailChange = (e) => {
    setMessage(null);
    formik.handleChange(e);
  };

  const handlePasswordChange = (e) => {
    setMessage(null);
    formik.handleChange(e);
  };

  const handleMicrosoftLogin = async () => {
    if (loading || ssoLoading || isResending || isTwoFaSetupPending) return;
    setSsoLoading(true);
    setMessage(null);
    try {
      await startMicrosoftSSO("login");
    } catch (e) {
      setSsoLoading(false);
      setMessage(getLoginFeedback(e, "microsoft"));
    }
  };

  const formDisabled =
    loading || ssoLoading || isResending || isTwoFaSetupPending;

  return (
    <Box sx={{ width: "100%", maxWidth: 400, mx: "auto" }}>
      <Typography
        component='h1'
        variant='h5'
        sx={{
          mb: 3,
          color: "text.primary",
          fontWeight: 700,
          fontSize: "24px",
          letterSpacing: "-0.2px",
        }}
      >
        Sign In
      </Typography>

      {message && (
        <AuthFeedback feedback={message} sx={{ mb: 2.5 }}>
          {message.canResend && (
            <Button
              variant='outlined'
              size='small'
              disabled={formDisabled}
              onClick={handleResendEmail}
              startIcon={
                isResending ? (
                  <CircularProgress size={14} color='inherit' />
                ) : (
                  <MailOutlineIcon />
                )
              }
              sx={{
                mt: 1.5,
                textTransform: "none",
                borderRadius: 1.5,
                fontWeight: 600,
              }}
            >
              {isResending ? "Sending email..." : "Resend verification email"}
            </Button>
          )}
        </AuthFeedback>
      )}

      <form noValidate onSubmit={formik.handleSubmit} aria-busy={formDisabled}>
        <TextField
          fullWidth
          id='email'
          name='email'
          label='Email Address'
          variant='outlined'
          autoComplete='email'
          inputProps={{
            inputMode: "email",
            autoCapitalize: "none",
            spellCheck: false,
          }}
          disabled={formDisabled}
          value={formik.values.email}
          onChange={handleEmailChange}
          onBlur={formik.handleBlur}
          error={formik.touched.email && Boolean(formik.errors.email)}
          helperText={formik.touched.email && formik.errors.email}
          sx={{ mb: 2 }}
          InputProps={{
            sx: outlinedInputFocusSx(theme),
          }}
          InputLabelProps={{
            sx: {
              color: "text.secondary",
              fontSize: "14px",
            },
          }}
        />

        <TextField
          fullWidth
          id='password'
          name='password'
          label='Password'
          type={showPassword ? "text" : "password"}
          variant='outlined'
          autoComplete='current-password'
          disabled={formDisabled}
          value={formik.values.password}
          onChange={handlePasswordChange}
          onFocus={() => setPasswordFocused(true)}
          onBlur={(e) => {
            setPasswordFocused(false);
            formik.handleBlur(e);
          }}
          error={formik.touched.password && Boolean(formik.errors.password)}
          helperText={formik.touched.password && formik.errors.password}
          sx={{ mb: 1 }}
          InputProps={{
            sx: outlinedInputFocusSx(theme),
            endAdornment: (
              <InputAdornment position='end'>
                <IconButton
                  onClick={handleClickShowPassword}
                  onMouseDown={(event) => event.preventDefault()}
                  disabled={formDisabled}
                  aria-label={showPassword ? "Hide password" : "Show password"}
                  edge='end'
                  sx={{
                    color: passwordFocused ? "primary.main" : "text.secondary",
                    transition: "color 0.15s ease",
                    "&:hover": {
                      color: "primary.main",
                      backgroundColor: alpha(theme.palette.primary.main, 0.06),
                    },
                  }}
                >
                  {showPassword ? <VisibilityOff /> : <Visibility />}
                </IconButton>
              </InputAdornment>
            ),
          }}
          InputLabelProps={{
            sx: {
              color: "text.secondary",
              fontSize: "14px",
            },
          }}
        />

        <Box
          sx={{
            display: "flex",
            justifyContent: "flex-end",
            mt: -0.5,
            mb: 2.5,
          }}
        >
          <Button
            variant='text'
            size='small'
            onClick={() => navigate("/password-reset")}
            disabled={formDisabled}
            sx={{
              fontSize: "13px",
              textTransform: "none",
              fontWeight: 500,
              px: 0,
              minWidth: 0,
              "&:hover": {
                backgroundColor: "transparent",
                textDecoration: "underline",
              },
            }}
          >
            Forgot password?
          </Button>
        </Box>

        <Button
          variant='contained'
          type='submit'
          disabled={formDisabled || !formik.isValid || !formik.dirty}
          sx={{
            width: "100%",
            minHeight: 48,
            py: 1.5,
            fontSize: "15px",
            fontWeight: 600,
            boxShadow: "none",
            borderRadius: 2,
            textTransform: "none",
            "&:hover": { boxShadow: "none" },
            transition: "background 0.2s ease",
          }}
        >
          {loading && (
            <CircularProgress size={18} color='inherit' sx={{ mr: 1 }} />
          )}
          {loading ? "Signing in..." : "Sign In"}
        </Button>
      </form>

      <MicrosoftAuthButton
        disabled={formDisabled}
        loading={ssoLoading}
        onClick={handleMicrosoftLogin}
      />

      <Snackbar
        open={snackbarOpen}
        autoHideDuration={4000}
        onClose={handleSnackbarClose}
      >
        <Alert
          onClose={handleSnackbarClose}
          severity='success'
          sx={{ width: "100%", borderRadius: "8px", fontSize: "14px" }}
          variant='filled'
        >
          Signed in successfully.
        </Alert>
      </Snackbar>
    </Box>
  );
};

export default LoginForm;
